import React, { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import GET_GSS_STUDENT_SETTINGS from 'graph/settings/gss/get-gss-student-settings-query';
import UPDATE_GSS_STUDENT_LANGUAGE_SETTING from 'graph/settings/gss/update-gss-student-language-setting-mutation';
import { updateCache } from 'graph/update-cache';
import REMOVE_STUDENT_SETTING from 'graph/settings/common/remove-student-setting-mutation';
import { LocaleStudentSettingProps } from '../../../../types';
import CommonLocaleSetting from '../common/LocaleSetting';

const APP_NAME = 'gss';

const getStudentGssSettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_GSS_STUDENT_SETTINGS,
      variables: { where: { studentId } },
      reducer: (cachedData, { updateGssStudentSettingLanguage }): { gssStudentSettings: any } => {
        const [{ value: locale }] = updateGssStudentSettingLanguage;
        return { gssStudentSettings: { ...cachedData?.gssStudentSettings, locale } };
      }
    }
  ])
});

const removeStudentSettingMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_GSS_STUDENT_SETTINGS,
      variables: { where: { studentId } },
      reducer: (cachedData): { gssStudentSettings: any } => {
        return { gssStudentSettings: { ...cachedData?.gssStudentSettings, locale: '' } };
      }
    }
  ])
});

const LocaleSetting: FC<LocaleStudentSettingProps> = props => {
  const { t } = useTranslation('LanguageOptionSetting');
  const studentId = +props.student.id;

  const [updateLocaleSetting] = useMutation(
    UPDATE_GSS_STUDENT_LANGUAGE_SETTING,
    getStudentGssSettingsMutationCacheUpdate(studentId)
  );

  const [removeLocaleSetting] = useMutation(
    REMOVE_STUDENT_SETTING,
    removeStudentSettingMutationCacheUpdate(studentId)
  );

  const removeGssLocale = useCallback(
    () =>
      removeLocaleSetting({
        variables: { where: { studentId, appName: APP_NAME, setting: 'locale' } }
      }),
    [studentId, removeLocaleSetting]
  );

  const updateGssLocale = useCallback(
    (value: string) => updateLocaleSetting({ variables: { data: { studentId, value } } }),
    [studentId, updateLocaleSetting]
  );

  const appLocaleSettingsProps = useMemo(
    () => ({
      description: t(
        'Change language setting for Touch & Flip Numbers. (Touch & Flip Numbers is the only module in the GSS Student Learning Apps available in Spanish.)'
      ),
      onRemove: removeGssLocale,
      onUpdate: updateGssLocale
    }),
    [t, updateGssLocale, removeGssLocale]
  );

  return <CommonLocaleSetting {...props} {...appLocaleSettingsProps} />;
};

export default LocaleSetting;
